/**
 * COMPONENTE GRAINS TICKER
 * =========================
 * Muestra las cotizaciones de granos (Soja, Maíz, Trigo)
 * Ahora obtiene los datos del backend
 */

import React, { useState, useEffect } from 'react';
import { TrendingUp, Wheat } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { api } from '../../services/api';

const GrainsTicker = () => {
  const [sectionRef, isVisible] = useScrollReveal({ threshold: 0.3 });
  
  // Estado para los datos de granos
  const [grains, setGrains] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar datos del backend
  useEffect(() => {
    const fetchGrains = async () => {
      try {
        const data = await api.getGrains();
        setGrains(data);
      } catch (error) {
        console.error('Error loading grains:', error);
        setGrains([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGrains();
  }, []);

  // Mostrar skeleton mientras carga
  if (loading) {
    return (
      <div className="bg-gradient-to-r from-green-900 via-green-700 to-green-900 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            <div className="animate-pulse bg-white/20 rounded-lg h-8 w-48"></div>
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white/20 rounded-xl h-24 w-32"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={sectionRef} className="bg-gradient-to-r from-green-900 via-green-700 to-green-900 py-6 relative overflow-hidden">
      {/* Pattern background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      {/* Animated shapes */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-green-400 rounded-full blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-emerald-400 rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 lg:gap-12">
          <div className={`flex items-center gap-2 md:gap-3 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <Wheat className="w-5 h-5 md:w-6 md:h-6 text-yellow-300" />
            <div className="text-white font-bold text-sm md:text-base uppercase tracking-wider">
              MERCADO DE GRANOS
            </div>
          </div>
          <div className="grid grid-cols-3 md:flex md:flex-row gap-3 md:gap-4 lg:gap-6 w-full md:w-auto">
            {grains.map((grain, index) => (
              <div
                key={index}
                className={`group flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-2 lg:space-x-3 bg-white/10 backdrop-blur-sm rounded-xl px-3 md:px-4 lg:px-6 py-2 md:py-3 border border-white/20 cursor-pointer
                transform hover:scale-110 hover:-translate-y-1 hover:bg-white/20 hover:border-yellow-400/50 hover:shadow-xl hover:shadow-yellow-400/20
                transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ 
                  transitionDelay: isVisible ? `${(index + 1) * 0.2}s` : '0s'
                }}
              >
                <span className="text-xl md:text-2xl group-hover:scale-125 transition-transform duration-300">{grain.icon}</span>
                <div className="text-center md:text-left">
                  <div className="text-white/80 text-[10px] md:text-xs font-semibold group-hover:text-yellow-300 transition-colors">{grain.name}</div>
                  <div className="flex flex-col md:flex-row items-center gap-1 md:gap-2">
                    <span className="text-white font-bold text-base md:text-lg lg:text-xl group-hover:text-yellow-300 transition-colors">{grain.value}</span>
                    <span className="flex items-center text-green-300 text-[10px] md:text-xs lg:text-sm font-medium bg-green-900/30 px-1.5 md:px-2 py-0.5 rounded group-hover:bg-green-800/50 group-hover:scale-105 transition-all">
                      <TrendingUp className="w-2 h-2 md:w-3 md:h-3 mr-0.5 md:mr-1 group-hover:animate-bounce" />
                      {grain.change}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrainsTicker;